import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateVoucherBatchMutation, useUpdateVoucherBatchMutation, useGetVoucherBatchQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface AdminVoucherBatchFormProps {
  type: 'card' | 'voucher';
}

const AdminVoucherBatchForm: React.FC<AdminVoucherBatchFormProps> = ({ type }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const title = type === 'card' ? 'Fesilah Cards' : 'Fesilah Vouchers';
  const listPath = type === 'card' ? '/admin/fesilah-cards' : '/admin/fesilah-vouchers';
  const defaultCurrency = process.env.REACT_APP_CURRENCY || 'AED';

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    card_value: 0,
    quantity: 0,
    expired_at: null as Date | null,
    currency: defaultCurrency,
  });

  const { data: batchData, isLoading: isBatchLoading } = useGetVoucherBatchQuery(id, {
    skip: !isEditMode,
  });

  const [createBatch, { isLoading: isCreating }] = useCreateVoucherBatchMutation();
  const [updateBatch, { isLoading: isUpdating }] = useUpdateVoucherBatchMutation();

  useEffect(() => {
    if (batchData?.data) {
      setFormData({
        name: batchData.data.name,
        name_ar: batchData.data.name_ar,
        card_value: parseFloat(batchData.data.card_value),
        quantity: batchData.data.quantity,
        expired_at: batchData.data.expired_at ? new Date(batchData.data.expired_at) : null,
        currency: batchData.data.currency || defaultCurrency,
      });
    }
  }, [batchData, defaultCurrency]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        batch_type: type,
        expired_at: formData.expired_at ? formData.expired_at.toISOString() : null,
      };

      if (isEditMode && id) {
        // For update, we might restrict changing value/quantity as per requirement
        // but backend validation should handle it if needed.
        await updateBatch({ id, data: payload }).unwrap();
        showSuccess('Batch updated successfully!');
      } else {
        await createBatch(payload).unwrap();
        showSuccess('Batch created successfully!');
      }
      navigate(listPath);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save batch. Please try again.');
    }
  };

  if (isEditMode && isBatchLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEditMode ? 'Edit Batch' : 'Add New Batch'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <button
                    type="button"
                    onClick={() => navigate(listPath)}
                    className="btn btn-link px-0 text-muted text-hover-primary"
                  >
                    {title}
                  </button>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{isEditMode ? 'Edit' : 'Add'}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  <h2>{isEditMode ? 'Edit Batch Details' : 'New Batch Details'}</h2>
                </div>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="row">
                  <div className="mb-5 col-md-6">
                    <label className="required form-label">Batch Name</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="e.g. Dubai Expo 2025"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-5 col-md-6">
                    <label className="required form-label">Batch Name (Arabic)</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="e.g. Dubai Expo 2025"
                      value={formData.name_ar}
                      onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-5 col-md-6">
                    <label className="required form-label">Card Value ({formData.currency})</label>
                    <div className="input-group input-group-solid">
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="0.00"
                        value={formData.card_value}
                        onChange={(e) => setFormData({ ...formData, card_value: parseFloat(e.target.value) })}
                        disabled={isEditMode}
                        required
                        min="0"
                        step="0.01"
                      />
                      <span className="input-group-text">
                        {formData.currency}
                      </span>
                    </div>
                    {isEditMode && <div className="text-muted fs-7 mt-2">Value cannot be changed after creation.</div>}
                  </div>

                  <div className="mb-5 col-md-6">
                    <label className="required form-label">Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      placeholder="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                      disabled={isEditMode} // Disable editing quantity after creation
                      required
                      min="1"
                    />
                    {isEditMode && <div className="text-muted fs-7 mt-2">Quantity cannot be changed after creation.</div>}
                  </div>

                  <div className="mb-5 col-md-6">
                    <label className="form-label">Expired At</label>
                    <div className="input-group input-group-solid">
                      <DatePicker
                        selected={formData.expired_at}
                        minDate={new Date()}
                        onChange={(date: Date | null) => setFormData({ ...formData, expired_at: date })}
                        className="form-control form-control-solid ps-15"
                        placeholderText="Select expiry date"
                        dateFormat="yyyy-MM-dd"
                        isClearable
                      />
                      <span className="input-group-text mt-n12 z-index-2">
                        <i className="fas fa-calendar-alt"></i>
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => navigate(listPath)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-base"
                      disabled={isCreating || isUpdating}
                    >
                      <span className="indicator-label">
                        {isEditMode ? 'Update' : 'Create'}
                      </span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVoucherBatchForm;
